import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useState, useEffect } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat } from '../helpers/GlobalHelper';

interface ProductionRow {
    id: number;
    productionDate: string;
    batchNo: string;
    productName: string;
    alternateName: string | null;
    packing: string;
    quantity: number;
    quantityLiter: number;
    unitName: string;
    createdBy: string;
}

const ProductionDetailsReport: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<ProductionRow[]>([]);
    const [formData, setFormData] = useState({
        idProjects: '',
        fromDate: '',
        toDate: '',
    });

    const [projects, setProjects] = useState([]);
    const [project, setProject] = useState({ label: 'Select Project', value: '' });

    useEffect(() => {
        getProjects();
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            setProject(projects[0]);
            setFormData({
                ...formData,
                idProjects: projects[0].value,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    const getProjects = () => {
        fetch(
            process.env.REACT_APP_API_URL + 'lead/projects',
            getRequestOptions()
        )
            .then((resp) => resp.json())
            .then((resp) => {
                let projectObj = resp.data.map((item: any) => {
                    item.label = item.name;
                    item.value = item.id;
                    return item;
                });
                setProjects(projectObj);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    const handleProjectChange = (project: any) => {
        setProject(project);
        if (project) {
            setFormData({ ...formData, idProjects: project.value });
        } else {
            setFormData({ ...formData, idProjects: '' });
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleViewReport = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/production-details-report',
                postRequestOptions(formData)
            );
            const data = await response.json();

            if (data.success) {
                setRows(data.data.rows ?? []);
            } else {
                setRows([]);
                if (data.errors) {
                    for (const i in data.errors) {
                        toast(<strong>{data.errors[i].join(' ')}</strong>, {
                            type: 'error',
                            position: 'bottom-right',
                            autoClose: 4000,
                        });
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const queryParams = new URLSearchParams(formData);

    const formatRowDate = (d: string) => {
        if (!d) return '';
        const x = new Date(d);
        if (Number.isNaN(x.getTime())) return d;
        return x.toLocaleDateString();
    };

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Title className="h5 mb-1">Production Details Report</Card.Title>
                    <p className="text-muted small mb-3">
                        Lists each production line with batch reference, packing, quantity, and liter total (Qty × product size).
                    </p>
                    <Card.Text>
                        <div className='row align-items-end'>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>

                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date<span className='required'></span></Form.Label>
                                    <Form.Control type="date" name="fromDate" value={formData.fromDate} onChange={handleChange} />
                                </Form.Group>
                            </div>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date<span className='required'></span></Form.Label>
                                    <Form.Control type="date" name="toDate" value={formData.toDate} onChange={handleChange} />
                                </Form.Group>
                            </div>
                            <div className='col-md-4'>
                                <div className="form-control-sm ps-0 mb-3">
                                    <button className='btn btn-sm btn-primary p-3 me-2' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                        )}
                                        <span><i className='fas fa-eye me-1'></i>View report</span>
                                    </button>
                                    <a
                                        className="btn btn-sm btn-outline-primary p-3"
                                        href={`${process.env.REACT_APP_BACKEND}production_details_report_download?${queryParams.toString()}`}
                                    >
                                        <i className='fas fa-download me-1'></i>Excel
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12'>
                    <Table responsive striped bordered hover size="sm" className="mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Date</th>
                                <th>Product Name</th>
                                <th>Batch No.</th>
                                <th>Packing</th>
                                <th className="text-end">Qty</th>
                                <th className="text-end">Qty-LTR</th>
                                <th>Unit</th>
                                <th>Created by</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted py-4">
                                        Choose project and date range, then click &quot;View report&quot; to load production lines.
                                    </td>
                                </tr>
                            )}
                            {rows.map((r) => (
                                <tr key={r.id}>
                                    <td>{formatRowDate(r.productionDate)}</td>
                                    <td>
                                        {r.productName}
                                        {r.alternateName ? (
                                            <span className="text-muted"> {r.alternateName}</span>
                                        ) : null}
                                    </td>
                                    <td>{r.batchNo}</td>
                                    <td>{r.packing}</td>
                                    <td className="text-end">{numberFormat(r.quantity)}</td>
                                    <td className="text-end">{numberFormat(r.quantityLiter)}</td>
                                    <td>{r.unitName}</td>
                                    <td>{r.createdBy}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>
        </>
    );
};

export default ProductionDetailsReport;
